import { initializeApp } from "firebase/app";
import { Capacitor } from "@capacitor/core";
import {
  browserLocalPersistence,
  getAuth,
  GoogleAuthProvider,
  initializeAuth,
  OAuthProvider,
} from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getDatabase } from "firebase/database";
import { getFunctions } from "firebase/functions";
import { initializeZunoAppCheck } from "./services/appCheckService";
import { markPerformance } from "./services/performanceTrace";
import { resolveFirebaseClientConfig } from "./services/firebaseClientConfig";
import { configureFirebasePlatformSecurity } from "./services/firebasePlatformSecurity";

markPerformance("FIREBASE_INIT_START", {}, { onlyOnce: true });

const { config: firebaseConfig, buildPlatform: firebaseBuildPlatform } = resolveFirebaseClientConfig();

configureFirebasePlatformSecurity({
  native: Capacitor.isNativePlatform(),
  platform: Capacitor.getPlatform?.() || "web",
  buildPlatform: firebaseBuildPlatform,
  firebaseApiKey: firebaseConfig.apiKey,
});

const app = initializeApp(firebaseConfig);

// The Web SDK remains the source of truth for the Zuno session. In Capacitor,
// initialize Auth explicitly with localStorage persistence so Firebase never
// selects IndexedDB inside WKWebView. This is the first and only Auth
// initialization for this Firebase app.
const auth = Capacitor.isNativePlatform()
  ? initializeAuth(app, { persistence: browserLocalPersistence })
  : getAuth(app);

const appCheckReady = initializeZunoAppCheck(app);
const ensureAppCheckReady = () => initializeZunoAppCheck(app);
const db = getFirestore(app);
const storage = getStorage(app);
const rtdb = getDatabase(app);
const functions = getFunctions(app);
const googleProvider = new GoogleAuthProvider();
const appleProvider = new OAuthProvider("apple.com");
appleProvider.addScope("email");
appleProvider.addScope("name");

// initializeAuth receives the persistence dependency before the Auth instance
// exists, so no later setPersistence call or second initialization is needed.
const authPersistenceReady = Promise.resolve({
  ok: true,
  mode: Capacitor.isNativePlatform() ? "native-browser-local" : "web-default",
});

markPerformance("FIREBASE_INIT_DONE", {
  projectId: firebaseConfig.projectId,
  platform: Capacitor.getPlatform?.() || (Capacitor.isNativePlatform() ? "native" : "web"),
}, { onlyOnce: true });

export {
  appCheckReady,
  ensureAppCheckReady,
  auth,
  authPersistenceReady,
  db,
  storage,
  rtdb,
  functions,
  googleProvider,
  appleProvider,
};
